import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Company } from '../../../services/api';

interface UpdateCompanyLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    minTransactionAmount: string;
    maxTransactionAmount: string;
    dailyLimit: string;
  }) => void;
  company: Company | null;
  isLoading?: boolean;
}

const UpdateCompanyLimitsModal: React.FC<UpdateCompanyLimitsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  company,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    minTransactionAmount: '',
    maxTransactionAmount: '',
    dailyLimit: '',
  });

  // Update form data when company changes or modal opens
  useEffect(() => {
    if (isOpen && company) {
      setFormData({
        minTransactionAmount: (company.minTransactionAmount / 100).toString(),
        maxTransactionAmount: (company.maxTransactionAmount / 100).toString(),
        dailyLimit: (company.dailyLimit / 100).toString(),
      });
    }
  }, [isOpen, company]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      minTransactionAmount: '',
      maxTransactionAmount: '',
      dailyLimit: '',
    });
    onClose();
  };

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-96 shadow-lg rounded-md bg-[#182233]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-white">Update Company Limits</h3>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-300"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-400">
            <strong>Company:</strong> {company.companyName}
          </p>
          <p className="text-sm text-gray-400">
            <strong>Current Balance:</strong> ${(company.valueAmount / 100).toFixed(2)}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Minimum Transaction Amount ($)
            </label>
            <input
              type="number"
              value={formData.minTransactionAmount}
              onChange={(e) => setFormData({ ...formData, minTransactionAmount: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter minimum transaction amount"
              min="0.01"
              step="0.01"
              required
              disabled={isLoading}
            />
            <p className="text-xs text-gray-400 mt-1">Minimum amount for each transaction</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Maximum Transaction Amount ($)
            </label>
            <input
              type="number"
              value={formData.maxTransactionAmount}
              onChange={(e) => setFormData({ ...formData, maxTransactionAmount: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter maximum transaction amount"
              min="0.01"
              step="0.01"
              required
              disabled={isLoading}
            />
            <p className="text-xs text-gray-400 mt-1">Maximum amount for each transaction</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Daily Spending Limit ($)
            </label>
            <input
              type="number"
              value={formData.dailyLimit}
              onChange={(e) => setFormData({ ...formData, dailyLimit: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter daily spending limit"
              min="0.01"
              step="0.01"
              required
              disabled={isLoading}
            />
            <p className="text-xs text-gray-400 mt-1">Maximum amount the company can spend per day</p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2] disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Limits'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCompanyLimitsModal;
